import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { useApp } from "@/contexts/AppContext";
import DataTable from "@/components/DataTable";
import { Button } from "@/components/ui/button";
import PageHeader from "@/components/PageHeader";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Checkbox } from "@/components/ui/checkbox";
import { Plus, Pencil, Trash2, BookOpen, Copy } from "lucide-react";
import { toast } from "sonner";

export default function Subjects() {
  const { ctx, meta } = useApp();
  const [rows, setRows] = useState([]);
  const [gradeFilter, setGradeFilter] = useState("");
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({
    stage: ctx.stage, year: ctx.year, grade: "", subject_name: "",
    max_score: 100, min_score: 50, year_work_score: 40, final_exam_score: 60,
    add_to_total: true, semester: Number(ctx.semester) || 1,
    subject_type: "مادة ذات تقييم ختامي", section: "عربي",
  });
  const [selectedGrades, setSelectedGrades] = useState([]);
  const [templates, setTemplates] = useState([]);
  const grades = meta?.grades_by_stage?.[ctx.stage] || [];
  const subjectTypes = meta?.subject_types || [];
  const sections = meta?.subject_sections || [];

  const load = () => {
    api.get("/subjects", { params: {
      stage: ctx.stage, year: ctx.year,
      ...(gradeFilter ? { grade: gradeFilter } : {}),
    } }).then(({ data }) => setRows(data)).catch((e) => toast.error(formatApiError(e)));
  };
  useEffect(load, [ctx.stage, ctx.year, gradeFilter]);
  useEffect(() => {
    api.get("/templates").then(({ data }) => setTemplates(data)).catch(() => {});
  }, []);

  const openNew = () => {
    setEditing(null);
    setSelectedGrades(grades.length ? [grades[0]] : []);
    setForm({
      stage: ctx.stage, year: ctx.year, grade: grades[0] || "",
      subject_name: "", max_score: 100, min_score: 50,
      year_work_score: 40, final_exam_score: 60, add_to_total: true,
      semester: 1, subject_type: subjectTypes[0] || "مادة ذات تقييم ختامي",
      section: "عربي", template_id: null,
    });
    setOpen(true);
  };
  const openEdit = async (row) => {
    setEditing(row);
    setForm({ ...row, template_id: null });
    setOpen(true);
    if (row.subject_type === "مادة ذات تقييم ختامي") {
      try {
        const { data } = await api.get("/subject-components", {
          params: {
            stage: row.stage, year: row.year, semester: row.semester,
            subject_name: row.subject_name, section: row.section, grade: row.grade,
          },
        });
        if (data.source === "custom" && data.template_id) {
          setForm((f) => ({ ...f, template_id: data.template_id }));
        }
      } catch (e) { /* لا شيء — يبقى بلا قالب مُحدَّد مسبقًا */ }
    }
  };
  const applyTemplateIfPicked = async (semester, subjectName, section) => {
    if (!form.template_id) return;
    try {
      await api.post("/subject-components/apply", null, {
        params: {
          stage: ctx.stage, year: ctx.year, semester,
          subject_name: subjectName, section, template_id: form.template_id,
        },
      });
    } catch (e) { /* لا نوقف الحفظ بسبب فشل ربط القالب — نبلّغ فقط */ toast.error("تم حفظ المادة، لكن فشل ربط القالب"); }
  };
  const save = async () => {
    try {
      if (editing) {
        const payload = { ...form, stage: ctx.stage, year: ctx.year };
        delete payload.template_id;
        await api.put(`/subjects/${editing.id}`, payload);
        await applyTemplateIfPicked(form.semester, form.subject_name, form.section);
        toast.success("تم تحديث المادة");
      } else {
        if (!selectedGrades.length) {
          toast.error("اختر صفًا واحدًا على الأقل");
          return;
        }
        let created = 0, skipped = 0;
        for (const g of selectedGrades) {
          const payload = { ...form, stage: ctx.stage, year: ctx.year, grade: g };
          delete payload.template_id;
          try {
            await api.post("/subjects", payload);
            await applyTemplateIfPicked(form.semester, form.subject_name, form.section);
            created++;
          } catch (e) {
            skipped++;
          }
        }
        if (created && !skipped) {
          toast.success(`تمت إضافة المادة لـ${created} صف بنجاح`);
        } else if (created && skipped) {
          toast.success(`تمت الإضافة لـ${created} صف، وتم تجاهل ${skipped} (موجودة بالفعل)`);
        } else {
          toast.error("لم تتم إضافة المادة لأي صف — تأكد أنها غير مكرَّرة");
        }
      }
      setOpen(false); load();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const del = async (row) => {
    if (!window.confirm(`حذف المادة «${row.subject_name}»؟`)) return;
    try { await api.delete(`/subjects/${row.id}`); toast.success("تم الحذف"); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  const copyToSem2 = async () => {
    const target = gradeFilter
      ? `الصف: ${gradeFilter}`
      : "جميع صفوف المرحلة";
    if (!window.confirm(`نسخ مواد الفصل الأول إلى الفصل الثاني لـ ${target}؟ سيتم تخطي المواد الموجودة بالفعل.`)) return;
    try {
      const { data } = await api.post("/subjects/copy-sem1-to-sem2", {
        stage: ctx.stage, year: ctx.year,
        ...(gradeFilter ? { grade: gradeFilter } : {}),
      });
      toast.success(`تم نسخ ${data.copied} مادة، تخطي ${data.skipped}`);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const columns = [
    { key: "subject_name", header: "المادة",
      render: (r) => <span className="font-medium">{r.subject_name}</span> },
    { key: "grade", header: "الصف" },
    { key: "semester", header: "الفصل", render: (r) => `الفصل ${r.semester}` },
    { key: "section", header: "القسم", render: (r) => (
      <span className={"inline-flex px-2 py-0.5 rounded-md text-[11px] font-medium " + (r.section === "لغات" ? "bg-teal-50 text-teal-700" : "bg-slate-100 text-slate-700")}>
        {r.section}
      </span>
    ) },
    { key: "subject_type", header: "نوع التقييم", render: (r) => <span className="text-xs text-muted-foreground">{r.subject_type}</span> },
    { key: "max_score", header: "النهاية", align: "end", render: (r) => <span className="tabular-nums">{r.max_score}</span> },
    { key: "min_score", header: "النجاح", align: "end", render: (r) => <span className="tabular-nums">{r.min_score}</span> },
    { key: "distribution", header: "أعمال / نهائي", align: "end",
      render: (r) => <span className="tabular-nums text-xs text-muted-foreground">{r.year_work_score}/{r.final_exam_score}</span> },
  ];

  return (
    <div className="space-y-6" data-testid="subjects-page">
      <PageHeader
        icon={BookOpen}
        overline="الشؤون الأكاديمية"
        title="المواد الدراسية"
        subtitle="إدارة المواد وتحديد الدرجات الكبرى، ودرجات النجاح، وتوزيع أعمال السنة والاختبار النهائي."
        badge={`${rows.length} مادة`}
        actions={
          <>
            <Select value={gradeFilter || "all"} onValueChange={(v) => setGradeFilter(v === "all" ? "" : v)}>
              <SelectTrigger
                className="w-[200px] h-10 bg-white/10 border-white/20 text-white hover:bg-white/15 rounded-lg"
                data-testid="subjects-grade-filter"
              >
                <SelectValue placeholder="كل الصفوف" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">كل الصفوف</SelectItem>
                {grades.map((g) => <SelectItem key={g} value={g}>{g}</SelectItem>)}
              </SelectContent>
            </Select>
            <Button
              onClick={copyToSem2} variant="outline"
              data-testid="subjects-copy-btn"
              className="h-10 bg-white/10 border-white/20 text-white hover:bg-white/15"
            >
              <Copy className="h-4 w-4 me-1.5" /> نسخ للفصل الثاني
            </Button>
            <Button
              onClick={openNew}
              data-testid="subjects-add-btn"
              className="h-10 bg-white text-[#0E7469] hover:bg-[#F4D06F] hover:text-[#083F38] font-semibold"
            >
              <Plus className="h-4 w-4 me-1.5" /> إضافة مادة
            </Button>
          </>
        }
      />

      <DataTable
        columns={columns} rows={rows}
        searchKeys={["subject_name", "grade"]}
        actions={(row) => (
          <div className="flex items-center gap-1 justify-end">
            <Button size="icon" variant="ghost" onClick={() => openEdit(row)} data-testid={`subject-edit-${row.id}`}>
              <Pencil className="h-4 w-4" />
            </Button>
            <Button size="icon" variant="ghost" className="text-destructive" onClick={() => del(row)} data-testid={`subject-del-${row.id}`}>
              <Trash2 className="h-4 w-4" />
            </Button>
          </div>
        )}
        emptyText="لا توجد مواد بعد. ابدأ بإضافة مادة."
        testId="subjects-table"
      />

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl" dir="rtl">
          <DialogHeader>
            <DialogTitle className="font-display text-xl">
              <BookOpen className="inline h-5 w-5 me-1.5" />
              {editing ? "تعديل مادة" : "إضافة مادة"}
            </DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-4 py-2">
            <div className="col-span-2 space-y-1.5">
              <Label>اسم المادة *</Label>
              <Input value={form.subject_name} onChange={(e) => setForm({ ...form, subject_name: e.target.value })} data-testid="subject-form-name" />
            </div>
            {editing ? (
              <SelectField label="الصف" value={form.grade} onValueChange={(v) => setForm({ ...form, grade: v })} options={grades} testId="subject-form-grade" />
            ) : (
              <div className="col-span-2 space-y-1.5">
                <div className="flex items-center justify-between">
                  <Label>الصفوف * (اختر واحدًا أو أكثر)</Label>
                  <div className="flex gap-2 text-xs">
                    <button type="button" className="text-primary hover:underline"
                            onClick={() => setSelectedGrades(grades)} data-testid="grades-select-all">
                      تحديد الكل
                    </button>
                    <button type="button" className="text-muted-foreground hover:underline"
                            onClick={() => setSelectedGrades([])}>
                      إلغاء التحديد
                    </button>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-2 rounded-lg border border-border p-3 max-h-40 overflow-y-auto">
                  {grades.map((g) => (
                    <label key={g} className="flex items-center gap-2 text-sm cursor-pointer">
                      <Checkbox
                        checked={selectedGrades.includes(g)}
                        onCheckedChange={(checked) => {
                          setSelectedGrades((prev) =>
                            checked ? [...prev, g] : prev.filter((x) => x !== g));
                        }}
                        data-testid={`grade-check-${g}`}
                      />
                      {g}
                    </label>
                  ))}
                </div>
                <p className="text-xs text-muted-foreground">
                  مثال: لإضافة "اللغة العربية" على كل الصفوف مرة واحدة، اضغط "تحديد الكل".
                </p>
              </div>
            )}
            <SelectField label="الفصل الدراسي" value={String(form.semester)} onValueChange={(v) => setForm({ ...form, semester: Number(v) })} options={["1", "2"]} labels={{ "1": "الفصل الأول", "2": "الفصل الثاني" }} testId="subject-form-sem" />
            <SelectField label="نوع المادة" value={form.subject_type} onValueChange={(v) => setForm({ ...form, subject_type: v })} options={subjectTypes} />
            <SelectField label="القسم" value={form.section} onValueChange={(v) => setForm({ ...form, section: v })} options={sections} />
            <NumberField label="النهاية العظمى" value={form.max_score} onChange={(v) => setForm({ ...form, max_score: v })} />
            <NumberField label="درجة النجاح" value={form.min_score} onChange={(v) => setForm({ ...form, min_score: v })} />
            <NumberField label="أعمال السنة" value={form.year_work_score} onChange={(v) => setForm({ ...form, year_work_score: v })} />
            <NumberField label="الاختبار النهائي" value={form.final_exam_score} onChange={(v) => setForm({ ...form, final_exam_score: v })} />
            {form.subject_type === "مادة ذات تقييم ختامي" && (
              <div className="col-span-2 space-y-1.5">
                <Label>قالب مكوّنات أعمال السنة</Label>
                <SelectField
                  value={form.template_id || "__none__"}
                  onValueChange={(v) => setForm({ ...form, template_id: v === "__none__" ? null : v })}
                  options={["__none__", ...templates.map((t) => t.id)]}
                  labels={{
                    __none__: "— بدون قالب (تخصيص يدوي) —",
                    ...Object.fromEntries(templates.map((t) => [t.id, `${t.name}  (${t.component_count} مكوّن)`])),
                  }}
                  testId="subject-form-template"
                />
                <p className="text-xs text-muted-foreground">
                  اختيار قالب يربط مكوّنات أعمال السنة (المواظبة، الواجبات...) بهذه المادة تلقائيًا،
                  بنفس أسلوب البرنامج المكتبي.
                </p>
              </div>
            )}
            <div className="col-span-2 flex items-center justify-between rounded-lg border border-border p-3 mt-2">
              <div>
                <div className="text-sm font-medium">تضاف للمجموع الكلي</div>
                <div className="text-xs text-muted-foreground">إن كانت المادة تحتسب ضمن مجموع الطالب</div>
              </div>
              <Switch checked={form.add_to_total} onCheckedChange={(v) => setForm({ ...form, add_to_total: v })} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>إلغاء</Button>
            <Button onClick={save} data-testid="subject-form-save">حفظ</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

    </div>
  );
}

function NumberField({ label, value, onChange }) {
  return (
    <div className="space-y-1.5">
      <Label>{label}</Label>
      <Input type="number" step="0.5" value={value ?? 0} onChange={(e) => onChange(parseFloat(e.target.value) || 0)} />
    </div>
  );
}
function SelectField({ label, value, onValueChange, options, labels, testId }) {
  return (
    <div className="space-y-1.5">
      <Label>{label}</Label>
      <Select value={value} onValueChange={onValueChange}>
        <SelectTrigger data-testid={testId}><SelectValue /></SelectTrigger>
        <SelectContent>
          {options.map((o) => (
            <SelectItem key={o} value={o}>{labels?.[o] ?? o}</SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}
